import { FunctionComponent, SVGProps } from 'react';

import { ReactComponent as FitnessBadgeSvg } from 'assets/images/collection/placeholder-fitness.svg';
import { ReactComponent as FoodBadgeSvg } from 'assets/images/collection/placeholder-food.svg';
import { ReactComponent as OfficeBadgeSvg } from 'assets/images/collection/placeholder-office.svg';
import DigitalHealthBg from 'assets/images/collection/bg-digital_health.png';
import FitnessActivitiesBg from 'assets/images/collection/bg-fitness_activities.png';
import FitnessEquipmentBg from 'assets/images/collection/bg-fitness_equipment.png';
import FoodServicesBg from 'assets/images/collection/bg-food_services.png';
import HomeOfficeBg from 'assets/images/collection/bg-home_office.png';
import WellbeingServicesBg from 'assets/images/collection/bg-wellbeing_services.png';

export const enum CATEGORY_IDS {
  CAREGIVING = 'caregiving',
  DIGITAL_HEALTH = 'digital_health',
  FINANCIAL_WELLBEING = 'financial_wellbeing',
  FITNESS_ACCESSORIES = 'fitness_accessories',
  FITNESS_ACTIVITIES = 'fitness_activities',
  FITNESS_EQUIPMENT = 'fitness_equipment',
  FOOD = 'food',
  FOOD_SERVICES = 'food_services',
  HOME_EQUIPMENT = 'home_equipment',
  HOME_OFFICE = 'home_office',
  MEDICAL_SERVICES = 'medical_services',
  OFFICE_SUPPLIES = 'office_supplies',
  OTHERS = 'others',
  SKILLS_DEVELOPMENT = 'skills_development',
  WELLBEING_SERVICES = 'wellbeing_services',
}

export const TARGET_CATEGORIES_MAP: {
  [key: string]: {
    id: string;
    label: string;
  };
} = Object.freeze({
  digital_health: {
    id: CATEGORY_IDS.DIGITAL_HEALTH,
    label: 'Digital Health',
  },
  fitness_activities: {
    id: CATEGORY_IDS.FITNESS_ACTIVITIES,
    label: 'Gym and Fitness',
  },
  caregiving: {
    id: CATEGORY_IDS.CAREGIVING,
    label: 'Family',
  },
  food_services: {
    id: CATEGORY_IDS.FOOD_SERVICES,
    label: 'Food',
  },
  wellbeing_services: {
    id: CATEGORY_IDS.WELLBEING_SERVICES,
    label: 'Wellbeing',
  },
});

export const ELIGIBLE_CATEGORIES = [
  'fitness_accessories',
  'fitness_activities',
  'fitness_equipment',
  'food_services',
  'home_office',
  'wellbeing_services',
];

export const POPULAR_CATEGORIES: {
  [key: string]: {
    background_image: string;
    description: string;
    id: string;
    pill_icon: FunctionComponent<SVGProps<SVGSVGElement>>;
    priority: number;
    title: string;
  };
} = Object.freeze({
  digital_health: {
    background_image: DigitalHealthBg,
    description: 'Deals on fitness, routine, and meditation apps',
    id: CATEGORY_IDS.DIGITAL_HEALTH,
    pill_icon: FoodBadgeSvg,
    priority: 6,
    title: 'Goodbye to stress',
  },
  fitness_activities: {
    background_image: FitnessActivitiesBg,
    description: 'Get discounts on gyms and class subscriptions!',
    id: CATEGORY_IDS.FITNESS_ACTIVITIES,
    pill_icon: FitnessBadgeSvg,
    priority: 4,
    title: 'Gyms and classes',
  },
  fitness_equipment: {
    background_image: FitnessEquipmentBg,
    description: 'Build your home gym or save on a smart watch!',
    id: CATEGORY_IDS.FITNESS_EQUIPMENT,
    pill_icon: FitnessBadgeSvg,
    priority: 2,
    title: 'Fitness equipment',
  },
  food_services: {
    background_image: FoodServicesBg,
    description: 'Get meal kits, groceries, or your favorite takeout.',
    id: CATEGORY_IDS.FOOD_SERVICES,
    pill_icon: FoodBadgeSvg,
    priority: 1,
    title: 'Food delivery',
  },
  home_office: {
    background_image: HomeOfficeBg,
    description: 'Deals on desks, chairs, and laptop accessories',
    id: CATEGORY_IDS.HOME_OFFICE,
    pill_icon: OfficeBadgeSvg,
    priority: 3,
    title: 'Home office',
  },
  wellbeing_services: {
    background_image: WellbeingServicesBg,
    description: 'Discounts on massage and relaxation equipment',
    id: CATEGORY_IDS.WELLBEING_SERVICES,
    pill_icon: OfficeBadgeSvg,
    priority: 5,
    title: 'Wellbeing',
  },
});

// NOTE: this is a sorted list and order matters
// used on store landing page and any other components that looks up against list of top categories per spend
// https://docs.google.com/spreadsheets/d/1LOUafnQsAb-U7Hnvc_A43fdUOGEVtio-Z97ww9RU72g/edit#gid=0
export const TOP_CATEGORIES: Array<{
  id: CATEGORY_IDS;
  name: string;
}> = [
  { id: CATEGORY_IDS.FOOD_SERVICES, name: 'Food Services' },
  { id: CATEGORY_IDS.HOME_OFFICE, name: 'Home Office' },
  { id: CATEGORY_IDS.FITNESS_EQUIPMENT, name: 'Fitness Equipment' },
  { id: CATEGORY_IDS.FITNESS_ACCESSORIES, name: 'Fitness Accessories' },
  { id: CATEGORY_IDS.DIGITAL_HEALTH, name: 'Digital Health' },
  { id: CATEGORY_IDS.WELLBEING_SERVICES, name: 'Wellbeing Services' },
  { id: CATEGORY_IDS.FITNESS_ACTIVITIES, name: 'Fitness Activities' },
  { id: CATEGORY_IDS.HOME_EQUIPMENT, name: 'Home Equipment' },
  { id: CATEGORY_IDS.SKILLS_DEVELOPMENT, name: 'Skills Development' },
  { id: CATEGORY_IDS.OTHERS, name: 'Miscellaneous' },
  { id: CATEGORY_IDS.FOOD, name: 'Food' },
  { id: CATEGORY_IDS.MEDICAL_SERVICES, name: 'Medical Services' },
  { id: CATEGORY_IDS.FINANCIAL_WELLBEING, name: 'Financial Wellbeing' },
];

export const enum CATEGORY_SORT_VALUES {
  NEW_ARRIVALS = 'recency',
  BEST_SELLERS = 'popularity',
  PRICE_ASC = 'price-asc',
  PRICE_DESC = 'price-desc',
}

export const CATEGORY_SORT_OPTIONS: Array<{
  value: CATEGORY_SORT_VALUES;
  name: string;
}> = [
  { value: CATEGORY_SORT_VALUES.NEW_ARRIVALS, name: 'New Arrivals' },
  { value: CATEGORY_SORT_VALUES.BEST_SELLERS, name: 'Best Sellers' },
  { value: CATEGORY_SORT_VALUES.PRICE_ASC, name: 'Price - Low to High' },
  { value: CATEGORY_SORT_VALUES.PRICE_DESC, name: 'Price - High to Low' },
];
